/**
 * @license Copyright (c) 2003-2023, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
 */
/**
 * @module emojis/ui/emojisnavigationview
 */
import { Collection } from 'ckeditor5/src/utils';
import { addListToDropdown, createDropdown, Model, FormHeaderView } from 'ckeditor5/src/ui';
/**
 * A class representing the navigation part of the emojis UI. It is responsible
 * for describing the feature and allowing the user to select a particular emoji group.
 */
export default class EmojisNavigationView extends FormHeaderView {
    /**
     * Creates an instance of the {@link module:emojis/ui/emojisnavigationview~EmojisNavigationView}
     * class.
     *
     * @param locale The localization services instance.
     * @param groupNames The names of the emoji groups and their displayed labels.
     */
    constructor(locale, groupNames) {
        super(locale);
        const t = locale.t;
        this.set('class', 'ck-emojis-navigation');
        this.groupDropdownView = this._createGroupDropdown(groupNames);
        this.groupDropdownView.panelPosition = locale.uiLanguageDirection === 'rtl' ? 'se' : 'sw';
        this.label = t('Emojis');
        this.children.add(this.groupDropdownView);
    }
    /**
     * Returns the name of the emoji group currently selected in the {@link #groupDropdownView}.
     */
    get currentGroupName() {
        return this.groupDropdownView.value;
    }
    /**
     * Focuses the emoji categories dropdown.
     */
    focus() {
        this.groupDropdownView.focus();
    }
    /**
     * Returns a dropdown that allows selecting emoji groups.
     *
     * @param groupNames The names of the emoji groups and their displayed labels.
     */
    _createGroupDropdown(groupNames) {
        const locale = this.locale;
        const t = locale.t;
        const dropdown = createDropdown(locale);
        const groupDefinitions = this._getEmojiGroupListItemDefinitions(dropdown, groupNames);
        const accessibleLabel = t('Emoji categories');
        dropdown.set('value', groupDefinitions.first.model.name);
        dropdown.buttonView.bind('label').to(dropdown, 'value', value => groupNames.get(value));
        dropdown.buttonView.set({
            isOn: false,
            withText: true,
            tooltip: accessibleLabel,
            class: ['ck-dropdown__button_label-width_auto'],
            ariaLabel: accessibleLabel,
            ariaLabelledBy: undefined
        });
        dropdown.on('execute', evt => {
            dropdown.value = evt.source.name;
        });
        dropdown.delegate('execute').to(this);
        addListToDropdown(dropdown, groupDefinitions, {
            ariaLabel: accessibleLabel,
            role: 'menu'
        });
        return dropdown;
    }
    /**
     * Returns list item definitions to be used in the emoji group dropdown
     * representing specific emoji groups.
     *
     * @param dropdown Dropdown view element
     * @param groupNames The names of the emoji groups and their displayed labels.
     */
    _getEmojiGroupListItemDefinitions(dropdown, groupNames) {
        const groupDefs = new Collection();
        for (const [name, label] of groupNames) {
            const model = new Model({
                name,
                label,
                withText: true,
                role: 'menuitemradio'
            });
            model.bind('isOn').to(dropdown, 'value', value => value === model.name);
            groupDefs.add({ type: 'button', model });
        }
        return groupDefs;
    }
}
